/**
 * The step-scoped, author-editable configuration for the workshop-prep ("Schedule") step —
 * the copy and Webex defaults that `WorkshopPrepPanel` renders. This is the single source of
 * truth: the runtime panel reads the effective config (see `resolveWorkshopPrepConfig`) and the
 * admin builder edits it, so a new campaign can rewrite every heading, the persona-priority
 * labels, the recording notice, the meeting-length choices and the meeting-title template with
 * ZERO code changes.
 *
 * Where the values live: the prepare step's `metadata.workshopPrepConfig` (a partial of
 * {@link WorkshopPrepConfig}). It is stored on the step, not the campaign, because the copy is
 * about this step's surface; the *people* it references (Cisco account-team roles, customer
 * personas) stay campaign-level reference objects edited by `TeamRolesEditor` / `PersonasEditor`.
 *
 * `DEFAULT_WORKSHOP_PREP_CONFIG` holds the values the panel shipped with (verbatim), so an
 * un-authored step renders exactly as before and the Frontier AI campaign stays byte-identical
 * until its own config is seeded. `resolveWorkshopPrepConfig` deep-merges a stored partial over
 * these defaults, tolerating older/partial records without a null-check at every read site.
 */

import type { CampaignCustomerPersona, PersonaRole } from '../../support/campaigns';

/** The persona-priority display labels, keyed by the persona's stored `role` enum. */
export interface WorkshopPrepPriorityLabels {
  must: string;
  should: string;
  nice: string;
}

/**
 * One persona this workshop-prep step selects from the campaign's persona catalogue, with the
 * priority it carries FOR THIS WORKSHOP. `ref` is the persona's stable reference value
 * ({@link personaSelectionRef}); `priority` is step-scoped and overrides the catalogue row's own
 * `role` when the panel renders its priority label.
 */
export interface WorkshopPrepPersonaSelection {
  ref: string;
  priority: PersonaRole;
}

export interface WorkshopPrepTeamConfig {
  /** Section heading for the Cisco account-team block. */
  title: string;
  /** Lead paragraph under the heading. */
  description: string;
  /**
   * The campaign `CampaignWorkshopTeamRole` names this workshop staffs from (a subset of the
   * catalogue). `undefined` (never authored) shows every non-hidden campaign role; an explicit
   * array — including empty — is authoritative.
   */
  roleRefs?: string[];
}

export interface WorkshopPrepPersonasConfig {
  /** Section heading for the customer-persona block. */
  title: string;
  /** Lead paragraph under the heading. */
  description: string;
  /**
   * How each persona's priority enum renders next to its name. The admin `PersonasEditor`
   * stores the enum (`must`/`should`/`nice`); this maps it to the customer-facing wording.
   */
  priorityLabels: WorkshopPrepPriorityLabels;
  /**
   * The campaign personas this workshop wants, each with its step-scoped priority (a subset of
   * the catalogue). `undefined` (never authored) shows every non-hidden campaign persona at its
   * catalogue `role`; an explicit array — including empty — is authoritative.
   */
  selected?: WorkshopPrepPersonaSelection[];
}

export interface WorkshopPrepScheduleConfig {
  /** Section heading for the Webex scheduling block. */
  title: string;
  /** Lead paragraph under the heading. */
  description: string;
  /** Bolded lead of the recording/transcription notice. */
  recordingNoticeLead: string;
  /** Remainder of the recording/transcription notice, rendered after the bold lead. */
  recordingNoticeBody: string;
  /** Selectable meeting lengths (minutes), in the order they appear. */
  durationOptions: number[];
  /** Pre-selected meeting length (minutes) for a new booking. */
  defaultDurationMinutes: number;
  /** Default meeting title. `{account}` is replaced with the account name. */
  meetingTitleTemplate: string;
  /**
   * An optional lead line for an explicit "People to schedule" roster in the schedule block, listing
   * the selected team roles + personas so the SE sees exactly who this workshop wants in the meeting.
   * Empty (the shipped default) hides the roster entirely, so an un-authored step — including Frontier
   * — renders unchanged; authoring any text opts the roster in.
   */
  peopleToScheduleNote: string;
}

export interface WorkshopPrepConfig {
  team: WorkshopPrepTeamConfig;
  personas: WorkshopPrepPersonasConfig;
  schedule: WorkshopPrepScheduleConfig;
}

/** A stored config: any subset of the shape may be present (older/partial records, sparse edits). */
export type WorkshopPrepConfigInput = {
  team?: Partial<WorkshopPrepTeamConfig>;
  personas?: Partial<WorkshopPrepPersonasConfig> & {
    priorityLabels?: Partial<WorkshopPrepPriorityLabels>;
  };
  schedule?: Partial<WorkshopPrepScheduleConfig>;
};

/** The step's `metadata` key under which a {@link WorkshopPrepConfigInput} is stored. */
export const WORKSHOP_PREP_CONFIG_KEY = 'workshopPrepConfig' as const;

/**
 * The values `WorkshopPrepPanel` shipped with, verbatim. Serve as the effective config for any
 * step that has not authored its own, so the rendered surface is unchanged until a campaign
 * overrides it.
 */
export const DEFAULT_WORKSHOP_PREP_CONFIG: WorkshopPrepConfig = {
  team: {
    title: 'Account Team',
    description:
      'Add the Cisco account team on this workshop and tag each with their role. More than one person can share a role, and everyone you add is invited to the meeting and notified about scheduling.',
  },
  personas: {
    title: 'Get the right people in the room',
    description:
      "Add the account contacts who'll join the workshop and tag each with the persona they fill. The personas below are what the workshop wants — more than one person can share a persona, and all are optional. Everyone you add is invited.",
    priorityLabels: {
      must: 'Highly Recommended',
      should: 'Recommended',
      nice: 'Optional',
    },
  },
  schedule: {
    title: 'Schedule the workshop',
    description:
      'Pick a date, time and length. Scheduling books a Webex meeting, invites the Account Team and everyone you added above, and messages them the details. You can edit or cancel a booked workshop.',
    recordingNoticeLead: 'This meeting will be recorded and transcribed.',
    recordingNoticeBody:
      'Let attendees know so both the Cisco team and the customer are aware before the workshop begins.',
    durationOptions: [90, 120, 150],
    defaultDurationMinutes: 120,
    meetingTitleTemplate: 'AI-Era Cyber Defense Workshop — {account}',
    // Empty by default so the roster is hidden until a campaign authors the note — keeps every
    // un-authored step (Frontier included) byte-identical.
    peopleToScheduleNote: '',
  },
};

/** Deep-merge a stored partial config over the shipped defaults. */
export function resolveWorkshopPrepConfig(
  input: WorkshopPrepConfigInput | null | undefined
): WorkshopPrepConfig {
  const d = DEFAULT_WORKSHOP_PREP_CONFIG;
  const src = input ?? {};
  return {
    team: {
      title: src.team?.title ?? d.team.title,
      description: src.team?.description ?? d.team.description,
      // Pass the selection through verbatim — `undefined` ("all roles") and an explicit array
      // (including empty) are distinct and both authoritative, so no default is applied.
      roleRefs: src.team?.roleRefs,
    },
    personas: {
      title: src.personas?.title ?? d.personas.title,
      description: src.personas?.description ?? d.personas.description,
      priorityLabels: {
        must: src.personas?.priorityLabels?.must ?? d.personas.priorityLabels.must,
        should: src.personas?.priorityLabels?.should ?? d.personas.priorityLabels.should,
        nice: src.personas?.priorityLabels?.nice ?? d.personas.priorityLabels.nice,
      },
      // As with `roleRefs`: pass the persona selection through untouched.
      selected: src.personas?.selected,
    },
    schedule: {
      title: src.schedule?.title ?? d.schedule.title,
      description: src.schedule?.description ?? d.schedule.description,
      recordingNoticeLead:
        src.schedule?.recordingNoticeLead ?? d.schedule.recordingNoticeLead,
      recordingNoticeBody:
        src.schedule?.recordingNoticeBody ?? d.schedule.recordingNoticeBody,
      durationOptions:
        src.schedule?.durationOptions && src.schedule.durationOptions.length > 0
          ? src.schedule.durationOptions
          : d.schedule.durationOptions,
      defaultDurationMinutes:
        src.schedule?.defaultDurationMinutes ?? d.schedule.defaultDurationMinutes,
      meetingTitleTemplate:
        src.schedule?.meetingTitleTemplate ?? d.schedule.meetingTitleTemplate,
      peopleToScheduleNote:
        src.schedule?.peopleToScheduleNote ?? d.schedule.peopleToScheduleNote,
    },
  };
}

/**
 * Format the "people to schedule" roster — the Cisco team roles + customer personas this workshop
 * wants — into a single human line. Shared by the runtime schedule block, the admin summary, and the
 * `{{webex.people}}` attribute token so all three read one definition. Each half is dropped when empty;
 * both empty ⇒ `''`.
 */
export function formatPeopleToSchedule(roleNames: string[], personaLabels: string[]): string {
  const roles = roleNames.map((n) => n.trim()).filter(Boolean);
  const personas = personaLabels.map((l) => l.trim()).filter(Boolean);
  const parts: string[] = [];
  if (roles.length) parts.push(`Cisco team: ${roles.join(', ')}`);
  if (personas.length) parts.push(`Customer: ${personas.join(', ')}`);
  return parts.join('; ');
}

/** Fill the `{account}` token in a meeting-title template. */
export function applyMeetingTitleTemplate(template: string, accountName: string): string {
  return template.replace(/\{account\}/g, accountName);
}

/**
 * The reference value stored for a persona selection: its stable DB id (as a string), falling back
 * to its label for a not-yet-persisted / legacy row. Matches `WorkshopPrepPanel`'s participant
 * `persona` convention so a selection and a participant reference the same value.
 */
export function personaSelectionRef(p: Pick<CampaignCustomerPersona, 'id' | 'label'>): string {
  return p.id != null ? String(p.id) : p.label;
}
